import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase, getAdminApiUrl } from '../lib/supabase';

interface UserProfileData {
  id: string;
  first_name: string;
  last_name: string;
  profile_type: string;
  business_name: string;
  abn_number: string;
  created_at: string;
  updated_at: string;
  country_code: string;
  phone: string;
  about_text: string;
  individual_image_url: string;
  business_image_url: string;
  business_expertise: string;
  default_profile: string;
  email: string;
  stripe_account_id: string;
  stripe_customer_id: string;
  expo_push_token: string;
  country_name: string;
  currency_code: string;
  currency_symbol: string;
  currency_name: string;
  is_admin: boolean;
  email_verified: boolean;
  email_verified_at: string;
  phone_verified: boolean;
  phone_verified_at: string;
  business_stripe_account_id: string;
  business_stripe_customer_id: string;
  business_rep_first_name: string;
  business_rep_last_name: string;
  city_name: string;
  state_name: string;
  city_latitude: number;
  city_longitude: number;
  state_latitude: number;
  state_longitude: number;
  phone_country_code: string;
  admin_role: string;
  user_metadata?: {
    force_logout?: number;
    force_logout_initiated_at?: string;
    force_logout_initiated_by?: string;
    [key: string]: any;
  };
}

interface Verification {
  id: string;
  user_id: string;
  verification_type: string;
  status: string;
  stripe_verification_session_id: string;
  verification_date: string;
  verification_details: any;
  created_at: string;
  updated_at: string;
  stripe_payment_intent_id: string;
  payment_status: string;
  payment_amount: number;
  payment_currency: string;
  payment_date: string;
}

interface BusinessVerification {
  id: string;
  user_id: string;
  abn: string;
  abn_status: string;
  abn_status_from: string | null;
  acn: string;
  entity_name: string;
  entity_type_code: string;
  business_names: any[];
  address_postcode: string;
  address_state: string;
  gst: boolean;
  abn_response: any;
  created_at: string;
  updated_at: string;
}

interface AdminNote {
  id: string;
  user_id: string;
  admin_id: string;
  note_content: string;
  note_type: 'general' | 'support' | 'warning' | 'ban' | 'unban' | 'verification' | 'payment' | 'other';
  is_important: boolean;
  created_at: string;
  updated_at: string;
  admin_email?: string;
  admin_first_name?: string;
  admin_last_name?: string;
  admin_business_name?: string;
  admin_role?: string;
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [businessVerifications, setBusinessVerifications] = useState<BusinessVerification[]>([]);
  const [expandedVerifications, setExpandedVerifications] = useState<Set<string>>(new Set());
  const [expandedBusinessVerifications, setExpandedBusinessVerifications] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [banDuration, setBanDuration] = useState<number>(24); // hours
  const [adminNotes, setAdminNotes] = useState<AdminNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteType, setNewNoteType] = useState<'general' | 'support' | 'warning' | 'ban' | 'unban' | 'verification' | 'payment' | 'other'>('general');
  const [newNoteImportant, setNewNoteImportant] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [banStatus, setBanStatus] = useState<{
    isBanned: boolean;
    banDuration?: string;
    bannedUntil?: string;
  } | null>(null);

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
      fetchVerifications();
      fetchBusinessVerifications();
      fetchAdminNotes();
    }
  }, [userId]);

  const updateBanStatus = (userData: any) => {
    // Check if user is banned by looking at user_metadata.banned_until
    const bannedUntil = userData.user_metadata?.banned_until;
    const bannedAt = userData.user_metadata?.banned_at;
    const unbannedAt = userData.user_metadata?.unbanned_at;
    
    // If user has been unbanned, they are not currently banned
    if (unbannedAt) {
      setBanStatus({
        isBanned: false,
        banDuration: undefined,
        bannedUntil: undefined
      });
      return;
    }
    
    if (bannedUntil) {
      const now = new Date();
      const banEndTime = new Date(bannedUntil);
      const isCurrentlyBanned = now < banEndTime;
      
      // Calculate ban duration from banned_at to banned_until
      let banDuration: string | undefined;
      if (bannedAt) {
        const banStartTime = new Date(bannedAt);
        const durationMs = banEndTime.getTime() - banStartTime.getTime();
        const durationHours = Math.round(durationMs / (1000 * 60 * 60));
        
        if (durationHours < 24) {
          banDuration = `${durationHours}h`;
        } else {
          const durationDays = Math.round(durationHours / 24);
          banDuration = `${durationDays}d`;
        }
      }
      
      setBanStatus({
        isBanned: isCurrentlyBanned,
        banDuration,
        bannedUntil: bannedUntil
      });
    } else {
      setBanStatus({
        isBanned: false,
        banDuration: undefined,
        bannedUntil: undefined
      });
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getUserById',
          userId: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
        updateBanStatus(data.user);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch user profile');
      }
    } catch (err) {
      setError('Error fetching user profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerifications = async () => {
    try {
      if (!userId) return;

      const { data, error } = await supabase
        .from('verifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return;
      }

      setVerifications(data || []);
    } catch (err) {
      // Error fetching verifications
    }
  };

  const fetchBusinessVerifications = async () => {
    try {
      if (!userId) return;

      const { data, error } = await supabase
        .from('business_verification')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return;
      }

      setBusinessVerifications(data || []);
    } catch (err) {
      // Error fetching business verifications
    }
  };

  const toggleVerificationExpansion = (verificationId: string) => {
    setExpandedVerifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(verificationId)) {
        newSet.delete(verificationId);
      } else {
        newSet.add(verificationId);
      }
      return newSet;
    });
  };

  const toggleBusinessVerificationExpansion = (verificationId: string) => {
    setExpandedBusinessVerifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(verificationId)) {
        newSet.delete(verificationId);
      } else {
        newSet.add(verificationId);
      }
      return newSet;
    });
  };

  const fetchAdminNotes = async () => {
    try {
      setNotesLoading(true);
      const { data: notes, error } = await supabase
        .from('admin_notes_with_details')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return;
      }

      setAdminNotes(notes || []);
    } catch (err) {
      // Error fetching admin notes - silently handle to prevent UI disruption
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      alert('Please enter a note');
      return;
    }

    try {
      setAddingNote(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        alert('No active session');
        return;
      }

      const { error } = await supabase
        .from('admin_notes')
        .insert({
          user_id: userId,
          admin_id: session.user.id,
          note_content: newNote.trim(),
          note_type: newNoteType,
          is_important: newNoteImportant
        })
        .select()
        .single();

      if (error) {
        alert('Failed to add note');
        return;
      }

      // Clear form
      setNewNote('');
      setNewNoteType('general');
      setNewNoteImportant(false);
      
      // Refresh notes
      await fetchAdminNotes();
      
      alert('Note added successfully');
    } catch (err) {
      alert('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('admin_notes')
        .delete()
        .eq('id', noteId);

      if (error) {
        alert('Failed to delete note');
        return;
      }

      // Refresh notes
      await fetchAdminNotes();
      alert('Note deleted successfully');
    } catch (err) {
      alert('Failed to delete note');
    }
  };

  const handleAdminAction = async (action: string, data?: any) => {
    try {
      setActionLoading(action);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          userId,
          data
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Special handling for different actions
        if (action === 'forceLogout') {
          const timestamp = new Date(result.force_logout_timestamp).toLocaleString();
          alert(`✅ Force logout successful!\n\nUser will be logged out immediately.\n\nTimestamp: ${timestamp}\n\nNote: The user will see a notification and be automatically signed out.`);
        } else if (action === 'banUser') {
          // Immediately set ban status for instant UI feedback
          const banDurationHours = result.ban_duration_hours || 24;
          const bannedUntil = result.banned_until || new Date(Date.now() + banDurationHours * 60 * 60 * 1000).toISOString();
          
          setBanStatus({
            isBanned: true,
            banDuration: `${banDurationHours}h`,
            bannedUntil: bannedUntil
          });
          
          alert(`✅ User banned successfully!\n\nBan Duration: ${result.ban_duration_hours || 'Unknown'} hours\nBanned Until: ${result.banned_until ? new Date(result.banned_until).toLocaleString() : 'Unknown'}\n\nThe user cannot access the platform until the ban expires.`);
        } else if (action === 'unbanUser') {
          // Immediately clear ban status for instant UI feedback
          setBanStatus({
            isBanned: false,
            banDuration: undefined,
            bannedUntil: undefined
          });
          alert(`✅ User unbanned successfully!\n\nUnbanned at: ${result.unbanned_at ? new Date(result.unbanned_at).toLocaleString() : 'Unknown'}\n\nUser access has been restored immediately.\n\nThe ban status banner will disappear and the user can now access the platform.`);
        } else {
          alert(`Action successful: ${result.message || 'Action completed'}`);
        }
        
        // Refresh profile data to update ban status
        await fetchUserProfile();
      } else {
        const errorData = await response.json();
        alert(`Action failed: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateLink = async (type: string) => {
    try {
      setActionLoading(`generateLink_${type}`);
      
      // Check if user has a valid email
      if (!profile?.email) {
        alert('No email address available for this user');
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const requestBody = {
        action: 'generateLink',
        data: {
          type,
          email: profile?.email
        }
      };
      


      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      


      if (response.ok) {
        const result = await response.json();
        
        if (result.message) {
          // New format: resetPasswordForEmail returns message and email
          alert(`✅ ${result.message}`);
        } else if (result.link) {
          // Old format: generateLink returns link
          alert(`Link generated successfully: ${result.link}`);
        } else if (result.properties?.action_link) {
          // Alternative format
          alert(`Link generated successfully: ${result.properties.action_link}`);
        } else {
          alert(`Action completed but response format is unexpected.`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to generate link: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendPhoneOTP = async () => {
    if (!profile?.phone) {
      alert('No phone number available for this user');
      return;
    }

    try {
      setActionLoading('sendPhoneOTP');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'sendPhoneOTP',
          data: {
            phone: profile.phone
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Phone OTP sent successfully: ${result.message}`);
      } else {
        const errorData = await response.json();
        alert(`Failed to send OTP: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              // Check if we have dashboard state to restore
              const dashboardState = location.state?.dashboardState || JSON.parse(localStorage.getItem('dashboardState') || '{}');
              if (Object.keys(dashboardState).length > 0) {
                navigate('/dashboard', { state: { preserveState: true, ...dashboardState } });
              } else {
                navigate('/dashboard');
              }
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">User Not Found</h1>
          <button
            onClick={() => {
              // Check if we have dashboard state to restore
              const dashboardState = location.state?.dashboardState || JSON.parse(localStorage.getItem('dashboardState') || '{}');
              if (Object.keys(dashboardState).length > 0) {
                navigate('/dashboard', { state: { preserveState: true, ...dashboardState } });
              } else {
                navigate('/dashboard');
              }
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">User Profile</h1>
              <p className="text-green-100 mt-1">
                {profile.business_name || `${profile.first_name} ${profile.last_name}`}
              </p>
            </div>
            <button
              onClick={() => {
                // Check if we have dashboard state to restore
                const dashboardState = location.state?.dashboardState || JSON.parse(localStorage.getItem('dashboardState') || '{}');
                if (Object.keys(dashboardState).length > 0) {
                  navigate('/dashboard', { state: { preserveState: true, ...dashboardState } });
                } else {
                  navigate('/dashboard');
                }
              }}
              className="px-4 py-2 bg-[#008f6b] text-white rounded-lg hover:bg-[#007a5a] transition-colors duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Ban Status Banner */}
      {banStatus?.isBanned && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <div className="flex items-center space-x-2">
                <h3 className="text-sm font-medium text-red-800">
                  🚫 User is Currently Banned
                </h3>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  BANNED
                </span>
              </div>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <strong>Ban Duration:</strong> {banStatus.banDuration}
                </p>
                {banStatus.bannedUntil && (
                  <p>
                    <strong>Ban Expires:</strong> {new Date(banStatus.bannedUntil).toLocaleString()}
                  </p>
                )}
                <p className="mt-1">
                  This user cannot access the platform until the ban expires or is manually removed.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Information Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Customer Information</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{profile.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.first_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.last_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Profile Type</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.profile_type || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Business Name</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.business_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">ABN Number</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.abn_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.country_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.city_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.state_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Admin Role</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.admin_role || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Status</label>
                  <div className="mt-1">
                    {banStatus?.isBanned ? (
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          🚫 BANNED
                        </span>
                        <span className="text-xs text-red-600">
                          ({banStatus.banDuration})
                        </span>
                      </div>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        ✅ Active
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Created At</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {profile.created_at ? new Date(profile.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Updated At</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {profile.updated_at ? new Date(profile.updated_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
              

              

            </div>
          </div>

          {/* User Actions Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">User Actions</h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Email Functions */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Email Functions</h3>
                <div className="space-y-3">
                  <div>
                    <button
                      onClick={() => handleGenerateLink('magiclink')}
                      disabled={actionLoading === 'generateLink_magiclink'}
                      className="w-full px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'generateLink_magiclink' ? 'Generating...' : 'Send Magic Link'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Sends a secure login link to the user's email address for passwordless authentication</p>
                  </div>
                  <div>
                    <button
                      onClick={() => handleGenerateLink('recovery')}
                      disabled={actionLoading === 'generateLink_recovery'}
                      className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'generateLink_recovery' ? 'Generating...' : 'Send Password Reset'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Sends a password reset link to the user's email address to help them regain account access</p>
                  </div>
                </div>
              </div>

              {/* Phone Functions */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Phone Functions</h3>
                <div>
                  <button
                    onClick={handleSendPhoneOTP}
                    disabled={actionLoading === 'sendPhoneOTP' || !profile.phone}
                    className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {actionLoading === 'sendPhoneOTP' ? 'Sending...' : 'Send Phone OTP'}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">Sends a one-time password (OTP) to the user's registered phone number for verification purposes</p>
                </div>
              </div>

              {/* Session Management */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Session Management</h3>
                <div className="space-y-3">
                  <div>
                    <button
                      onClick={() => handleAdminAction('forceLogout')}
                      disabled={actionLoading === 'forceLogout'}
                      className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'forceLogout' ? 'Processing...' : 'Force Logout'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Immediately terminates the user's current session and logs them out from all devices</p>
                    
                    {/* Force Logout Status Indicator */}
                    {profile.user_metadata?.force_logout && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <span className="text-xs text-red-700 font-medium">Force Logout Active</span>
                        </div>
                        <p className="text-xs text-red-600 mt-1">
                          Last force logout: {new Date(profile.user_metadata.force_logout).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={banDuration}
                        onChange={(e) => setBanDuration(Number(e.target.value))}
                        min="1"
                        max="8760"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Hours"
                      />
                      <button
                        onClick={() => handleAdminAction('banUser', { banDuration: banDuration * 3600 })}
                        disabled={actionLoading === 'banUser'}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        {actionLoading === 'banUser' ? 'Banning...' : 'Ban User'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Temporarily restricts user access to the platform for the specified duration (1-8760 hours)</p>
                  </div>
                  
                  <div>
                    <button
                      onClick={() => handleAdminAction('unbanUser')}
                      disabled={actionLoading === 'unbanUser'}
                      className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'unbanUser' ? 'Unbanning...' : 'Unban User'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Immediately removes any active ban and restores user access to the platform</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Verification Section - Only show for individual profiles */}
        {profile.profile_type !== 'business' && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Verification History</h2>
              </div>
              <div className="p-6">
                {verifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900">No verifications found</p>
                    <p className="text-sm text-gray-500">This user hasn't completed any verification processes yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Verification Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Verification Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {verifications.map((verification) => (
                          <React.Fragment key={verification.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    verification.verification_type === 'individual_identity' 
                                      ? 'bg-blue-100 text-blue-800'
                                      : verification.verification_type === 'business_identity'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {verification.verification_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  verification.status === 'verified' 
                                    ? 'bg-green-100 text-green-800'
                                    : verification.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : verification.status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {verification.status || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  verification.payment_status === 'paid' 
                                    ? 'bg-green-100 text-green-800'
                                    : verification.payment_status === 'unpaid'
                                    ? 'bg-red-100 text-red-800'
                                    : verification.payment_status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {verification.payment_status || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {verification.payment_amount && verification.payment_currency 
                                  ? `${verification.payment_amount} ${verification.payment_currency}`
                                  : 'N/A'
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {verification.verification_date 
                                  ? new Date(verification.verification_date).toLocaleDateString()
                                  : 'N/A'
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {verification.created_at 
                                  ? new Date(verification.created_at).toLocaleDateString()
                                  : 'N/A'
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => toggleVerificationExpansion(verification.id)}
                                  className="flex items-center justify-center cursor-pointer p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                                >
                                  <svg 
                                    className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${expandedVerifications.has(verification.id) ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                            
                            {/* Verification Details Row (expanded) */}
                            {expandedVerifications.has(verification.id) && (
                              <tr key={`expanded-${verification.id}`}>
                                <td colSpan={7} className="px-6 py-4 bg-gray-50">
                                  <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Verification Details</h4>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Stripe Information</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <span className="text-gray-600">Verification Session ID: </span>
                                            <span className="font-medium text-xs font-mono">{verification.stripe_verification_session_id || 'N/A'}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">Payment Intent ID: </span>
                                            <span className="font-medium text-xs font-mono">{verification.stripe_payment_intent_id || 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Payment Information</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <span className="text-gray-600">Payment Status: </span>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                              verification.payment_status === 'paid' 
                                                ? 'bg-green-100 text-green-800'
                                                : verification.payment_status === 'unpaid'
                                                ? 'bg-red-100 text-red-800'
                                                : verification.payment_status === 'pending'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                              {verification.payment_status || 'N/A'}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">Payment Date: </span>
                                            <span className="font-medium text-xs">{verification.payment_date ? new Date(verification.payment_date).toLocaleDateString() : 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <span className="text-gray-600">Amount: </span>
                                            <span className="font-medium">{verification.payment_amount && verification.payment_currency 
                                              ? `${verification.payment_amount} ${verification.payment_currency}`
                                              : 'N/A'
                                            }</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">Verification Date: </span>
                                            <span className="font-medium text-xs">{verification.verification_date ? new Date(verification.verification_date).toLocaleDateString() : 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Business Verification Section - Only show for business profiles */}
        {profile.profile_type === 'business' && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Business Verification History</h2>
              </div>
              <div className="p-6">
                {businessVerifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900">No business verifications found</p>
                    <p className="text-sm text-gray-500">This business hasn't completed any verification processes yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Entity Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ABN
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ACN
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ABN Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Entity Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            GST
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            State
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {businessVerifications.map((verification) => (
                          <React.Fragment key={verification.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {verification.entity_name || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 font-mono">
                                  {verification.abn || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 font-mono">
                                  {verification.acn || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  verification.abn_status === 'Active' 
                                    ? 'bg-green-100 text-green-800'
                                    : verification.abn_status === 'Cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {verification.abn_status || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {verification.entity_type_code || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  verification.gst 
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {verification.gst ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {verification.address_state || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {verification.created_at 
                                  ? new Date(verification.created_at).toLocaleDateString()
                                  : 'N/A'
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => toggleBusinessVerificationExpansion(verification.id)}
                                  className="flex items-center justify-center cursor-pointer p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                                >
                                  <svg 
                                    className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${expandedBusinessVerifications.has(verification.id) ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                            
                            {/* Business Verification Details Row (expanded) */}
                            {expandedBusinessVerifications.has(verification.id) && (
                              <tr key={`expanded-business-${verification.id}`}>
                                <td colSpan={9} className="px-6 py-4 bg-gray-50">
                                  <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Business Verification Details</h4>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Entity Information</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <span className="text-gray-600">Entity Name: </span>
                                            <span className="font-medium">{verification.entity_name || 'N/A'}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">ABN: </span>
                                            <span className="font-medium font-mono text-xs">{verification.abn || 'N/A'}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">ACN: </span>
                                            <span className="font-medium font-mono text-xs">{verification.acn || 'N/A'}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">Entity Type: </span>
                                            <span className="font-medium">{verification.entity_type_code || 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Status & Registration</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <span className="text-gray-600">ABN Status: </span>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                              verification.abn_status === 'Active' 
                                                ? 'bg-green-100 text-green-800'
                                                : verification.abn_status === 'Cancelled'
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                              {verification.abn_status || 'Unknown'}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">GST Registered: </span>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                              verification.gst 
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                              {verification.gst ? 'Yes' : 'No'}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">State: </span>
                                            <span className="font-medium">{verification.address_state || 'N/A'}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">Postcode: </span>
                                            <span className="font-medium">{verification.address_postcode || 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Timestamps</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <span className="text-gray-600">Created: </span>
                                            <span className="font-medium text-xs">{verification.created_at ? new Date(verification.created_at).toLocaleString() : 'N/A'}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">Updated: </span>
                                            <span className="font-medium text-xs">{verification.updated_at ? new Date(verification.updated_at).toLocaleString() : 'N/A'}</span>
                                          </div>
                                          {verification.abn_status_from && (
                                            <div>
                                              <span className="text-gray-600">Status From: </span>
                                              <span className="font-medium text-xs">{verification.abn_status_from}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* ABN Response Details */}
                                    {verification.abn_response && (
                                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">ABN Response Data</h6>
                                        <pre className="text-xs text-gray-700 bg-gray-50 p-2 rounded overflow-x-auto">
                                          {JSON.stringify(verification.abn_response, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Admin Notes Section */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Admin Notes</h2>
              <p className="text-sm text-gray-500 mt-1">Add and view interaction notes for this user</p>
            </div>
            
            {/* Add New Note Form */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add New Note</label>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter your note here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note Type</label>
                    <select
                      value={newNoteType}
                      onChange={(e) => setNewNoteType(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="general">General</option>
                      <option value="support">Support</option>
                      <option value="warning">Warning</option>
                      <option value="ban">Ban</option>
                      <option value="unban">Unban</option>
                      <option value="verification">Verification</option>
                      <option value="payment">Payment</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="important"
                      checked={newNoteImportant}
                      onChange={(e) => setNewNoteImportant(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="important" className="ml-2 block text-sm text-gray-700">
                      Mark as Important
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </div>
            </div>

            {/* Notes List */}
            <div className="px-6 py-4">
              {notesLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading notes...</p>
                </div>
              ) : adminNotes.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500">No notes yet. Add the first note above.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {adminNotes.map((note) => (
                    <div key={note.id} className={`border rounded-lg p-4 ${note.is_important ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            note.note_type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            note.note_type === 'ban' ? 'bg-red-100 text-red-800' :
                            note.note_type === 'unban' ? 'bg-green-100 text-green-800' :
                            note.note_type === 'support' ? 'bg-blue-100 text-blue-800' :
                            note.note_type === 'verification' ? 'bg-purple-100 text-purple-800' :
                            note.note_type === 'payment' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {note.note_type.charAt(0).toUpperCase() + note.note_type.slice(1)}
                          </span>
                          {note.is_important && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              ⚠️ Important
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="Delete note"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      
                      <p className="text-gray-900 mb-3 whitespace-pre-wrap">{note.note_content}</p>
                      
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <div>
                          <span className="font-medium">
                            {note.admin_business_name || 
                             (note.admin_first_name && note.admin_last_name ? 
                              `${note.admin_first_name} ${note.admin_last_name}` : 
                              note.admin_email)}
                          </span>
                          {note.admin_role && (
                            <span className="ml-2 px-1 py-0.5 bg-gray-100 rounded text-gray-600">
                              {note.admin_role}
                            </span>
                          )}
                        </div>
                        <div>
                          {new Date(note.created_at).toLocaleString()}
                          {note.updated_at !== note.created_at && (
                            <span className="ml-2 text-gray-400">(edited)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
